import React from "react";
import { Modal, Avatar, Button } from "antd";
import {
  LogoutOutlined,
  StarOutlined,
  LikeOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
  FileTextOutlined,
  BookOutlined,
  CustomerServiceOutlined,
  QuestionCircleFilled,
} from "@ant-design/icons";
import { User } from "@interfaces/user.interface";
import moment from "moment-timezone";
import styled from "styled-components";
import { HStack } from "@chakra-ui/react";

interface UserProfileModalProps {
  user: User;
  visible: boolean;
  onClose: () => void;
  onLogout: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({
  user,
  visible,
  onClose,
  onLogout,
}) => {
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    return moment(dateString).format("DD/MM/YYYY");
  };

  return (
    <Container>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        width={600}
        centered
        styles={{
          body: {
            padding: 0,
          },
        }}
      >
        <div style={{ backgroundColor: "#f6f7f9", padding: "16px" }}>
          {/* Header/Profile Section */}
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <Avatar
              src={user.socialite_avatar || user.avatar}
              size={60}
              style={{
                backgroundColor: "#0573ff",
                marginBottom: 12,
                border: "3px solid #fff",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              {!(user.socialite_avatar || user.avatar) &&
                user.full_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <div
              style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#000",
                marginBottom: 4,
              }}
            >
              {user.full_name}
            </div>
            <div style={{ fontSize: 14, color: "#666" }}>ID: {user.id}</div>
          </div>

          {/* Account Information Section */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 14, color: "#666" }}>
                Loại tài khoản:
              </span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>
                {user.type_name || "Tài khoản thường"}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 14, color: "#666" }}>
                Thành viên từ:
              </span>
              <span style={{ fontSize: 14, color: "#000" }}>
                {formatDate(user.created_at)}
              </span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 14, color: "#666" }}>Hạn dùng:</span>
              <span style={{ fontSize: 14, color: "#000" }}>
                {formatDate(user.expired_at)}
              </span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, color: "#666" }}>
                Đơn vị công tác:
              </span>
              <span style={{ fontSize: 14, color: "#000" }}>
                {user.work_address || "-"}
              </span>
            </div>
          </div>

          {/* Engagement Metrics Section */}
          <div
            style={{
              backgroundColor: "#fff",
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              <HStack alignItems="center" justifyContent="center">
                <StarOutlined style={{ fontSize: 20, color: "#FFC107" }} />
                <div style={{ fontSize: 12, color: "#666" }}>Điểm số</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000" }}>
                  {user.point || 0}
                </div>
              </HStack>
              <HStack alignItems="center" justifyContent="center">
                <LikeOutlined style={{ fontSize: 20, color: "#2196F3" }} />
                <div style={{ fontSize: 12, color: "#666" }}>Thích</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000" }}>
                  {user.question_reactions_count || 0}
                </div>
              </HStack>
              <HStack alignItems="center" justifyContent="center">
                <QuestionCircleOutlined
                  style={{ fontSize: 20, color: "#F44336" }}
                />
                <div style={{ fontSize: 12, color: "#666" }}>Câu hỏi</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000" }}>
                  {user.questions_count || 0}
                </div>
              </HStack>
              <HStack alignItems="center" justifyContent="center">
                <MessageOutlined style={{ fontSize: 20, color: "#4CAF50" }} />
                <div style={{ fontSize: 12, color: "#666" }}>Trả lời</div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#000" }}>
                  {user.answers_count || 0}
                </div>
              </HStack>
            </div>
          </div>

          {/* Action Buttons Section */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <Button
              style={{
                height: 80,
                backgroundColor: "#E8F5E9",
                border: "none",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <FileTextOutlined style={{ fontSize: 24, color: "#000" }} />
              <span style={{ fontSize: 12, color: "#000" }}>Nhiệm vụ</span>
            </Button>
            <Button
              style={{
                height: 80,
                backgroundColor: "#E8F5E9",
                border: "none",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <BookOutlined style={{ fontSize: 24, color: "#000" }} />
              <span style={{ fontSize: 12, color: "#000" }}>
                Bài viết đã lưu
              </span>
            </Button>
            <Button
              style={{
                height: 80,
                backgroundColor: "#E8F5E9",
                border: "none",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <QuestionCircleFilled style={{ fontSize: 24, color: "#000" }} />
              <span style={{ fontSize: 12, color: "#000" }}>Đặt câu hỏi</span>
            </Button>
            <Button
              style={{
                height: 80,
                backgroundColor: "#E8F5E9",
                border: "none",
                borderRadius: 8,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              <CustomerServiceOutlined
                style={{ fontSize: 24, color: "#000" }}
              />
              <span style={{ fontSize: 12, color: "#000" }}>Liên hệ admin</span>
            </Button>
          </div>

          {/* Logout Button */}
          <Button
            type="primary"
            block
            size="large"
            icon={<LogoutOutlined />}
            onClick={onLogout}
            style={{
              backgroundColor: "#0573ff",
              border: "none",
              height: 48,
              borderRadius: 8,
              fontWeight: 600,
            }}
          >
            Đăng xuất
          </Button>
        </div>
      </Modal>
    </Container>
  );
};

const Container = styled.div`
  .ant-modal-content {
    padding: 0;
  }
`;
export default UserProfileModal;
